'use client';

import Icon from '@/components/ui/AppIcon';

interface ProcessStep {
  id: number;
  title: string;
  duration: string;
  description: string;
  icon: string;
  details: string[];
}

interface ProcessTimelineProps {
  steps: ProcessStep[];
  activeStep: number | null;
  onStepClick: (stepId: number) => void;
  isHydrated: boolean;
}

export default function ProcessTimeline({ steps, activeStep, onStepClick, isHydrated }: ProcessTimelineProps) {
  return (
    <div className="max-w-5xl mx-auto">
      <div className="space-y-8">
        {steps.map((step, index) => (
          <div key={step.id} className="relative">
            {/* Connector Line */}
            {index < steps.length - 1 && (
              <div className="absolute left-6 top-16 w-0.5 h-full bg-border hidden lg:block" />
            )}

            {/* Step Card */}
            <div
              className={`bg-card rounded-xl shadow-card hover:shadow-elevated transition-smooth cursor-pointer ${
                activeStep === step.id ? 'ring-2 ring-primary' : ''
              }`}
              onClick={() => onStepClick(step.id)}
            >
              <div className="p-6 lg:p-8">
                <div className="flex items-start space-x-4 lg:space-x-6">
                  {/* Step Number & Icon */}
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 lg:w-16 lg:h-16 bg-primary rounded-full flex items-center justify-center text-white font-bold text-lg lg:text-xl">
                      {step.id}
                    </div>
                    <div className="mt-4 w-12 h-12 lg:w-16 lg:h-16 bg-secondary/10 rounded-lg flex items-center justify-center">
                      <Icon name={step.icon as any} size={24} className="text-secondary" />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="text-xl lg:text-2xl font-semibold text-primary">
                        {step.title}
                      </h3>
                      <span className="flex-shrink-0 ml-4 px-3 py-1 bg-accent/10 text-accent text-sm font-medium rounded-full">
                        {step.duration}
                      </span>
                    </div>
                    <p className="text-text-secondary mb-4">{step.description}</p>

                    {/* Expandable Details */}
                    {isHydrated && activeStep === step.id && (
                      <div className="mt-4 p-4 bg-surface rounded-lg border border-border">
                        <h4 className="text-sm font-semibold text-primary mb-3">
                          Co zahrnuje tento krok:
                        </h4>
                        <ul className="space-y-2">
                          {step.details.map((detail, idx) => (
                            <li key={idx} className="flex items-start space-x-2 text-sm text-text-secondary">
                              <Icon name="CheckCircleIcon" size={16} className="text-success mt-0.5 flex-shrink-0" />
                              <span>{detail}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Expand Indicator */}
                    <button
                      className="mt-4 text-sm text-primary font-medium flex items-center space-x-1 hover:text-secondary transition-smooth"
                      onClick={(e) => {
                        e.stopPropagation();
                        onStepClick(step.id);
                      }}
                    >
                      <span>{activeStep === step.id ? 'Skrýt detaily' : 'Zobrazit detaily'}</span>
                      <Icon
                        name="ChevronDownIcon"
                        size={16}
                        className={`text-current transition-transform ${
                          activeStep === step.id ? 'rotate-180' : ''
                        }`}
                      />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}